import { flexRender, Table as ReactTable, Row } from '@tanstack/react-table';
import Table, { TableBody, TableHeader } from '@ingka/table';
import SSRIcon from '@ingka/ssr-icon';
import ChevronDown from '@ingka/ssr-icon/paths/chevron-down';
import ChevronUp from '@ingka/ssr-icon/paths/chevron-up';

import styles from './index.module.css';
import { useTranslation } from 'react-i18next';
import { TablePaginationControls } from './pagination';

type TableMeta = {
  sticky?: boolean;
  left?: number;
  headerClassName?: string;
};

type TableProps<T> = {
  table: ReactTable<T>;
  onClickRow?: (row: Row<T>) => void;
  className?: string;
};

export function SkapaTable<T>({ table, onClickRow, className }: TableProps<T>) {
  // Hooks
  const { t } = useTranslation();

  return (
    <div className="w-full overflow-x-auto">
      <Table fullWidth className="w-full">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const meta = header.column.columnDef.meta as
                  | TableMeta
                  | undefined;
                const isSticky = meta?.sticky;
                const stickyLeft = meta?.left;
                return (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className={`align-middle break-words whitespace-normal ${
                      header.column.getCanSort() ? 'cursor-pointer' : ''
                    } ${header.column.columnDef.meta?.headerClassName || ''}`}
                    style={{
                      ...(isSticky
                        ? {
                            position: 'sticky' as const,
                            left: `${stickyLeft}px`,
                            zIndex: 2,
                            backgroundColor: 'rgb(var(--colour-neutral-1))',
                          }
                        : {}),
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {header.isPlaceholder ? null : (
                      <div className="flex items-center justify-between text-base py-2">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.getCanSort() && (
                          <div className="flex flex-col ml-[10px] h-6">
                            <SSRIcon
                              paths={ChevronUp}
                              className={
                                styles[
                                  header.column.getIsSorted() === 'asc'
                                    ? 'fill--selected'
                                    : 'fill'
                                ]
                              }
                            />
                            <SSRIcon
                              paths={ChevronDown}
                              className={
                                styles[
                                  header.column.getIsSorted() === 'desc'
                                    ? 'fill--selected'
                                    : 'fill'
                                ]
                              }
                            />
                          </div>
                        )}
                        {/* header.column.getIsSorted() */}
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </TableHeader>

        <TableBody striped>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} onClick={() => onClickRow && onClickRow(row)}>
              {row.getVisibleCells().map((cell) => {
                const meta = cell.column.columnDef.meta as TableMeta;
                const isSticky = meta?.sticky;
                const stickyLeft = meta?.left;
                return (
                  <td
                    key={cell.id}
                    className="align-middle break-words whitespace-normal"
                    style={
                      isSticky
                        ? {
                            position: 'sticky',
                            left: `${stickyLeft}px`,
                            zIndex: 1,
                            backgroundColor:
                              row.index % 2 === 0
                                ? 'rgb(var(--colour-neutral-2))'
                                : 'rgb(var(--colour-neutral-1))',
                          }
                        : undefined
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
            </tr>
          ))}
        </TableBody>
      </Table>

      {table.getPaginationRowModel().rows.length > 0 && (
        <TablePaginationControls
          className="gap-2 mt-3 px-4 pb-10"
          table={table}
        />
      )}

      {table.getRowModel().rows.length === 0 && (
        <div className="py-10 text-center text-skapa-text-2">
          {t('label.noData')}
        </div>
      )}
    </div>
  );
}
